import { z } from 'zod/v4';

const LIST_RISK_IMPACT_AREAS_DEFAULT_PAGE_SIZE = 15;
const LIST_RISK_IMPACT_AREAS_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/impact-areas` querystring, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/impact-area.js`'s
 * `list.querystring`.
 *
 * `sort` is dropped: the legacy `sortingColumnsMapping` was `{}`, so a sort was
 * always accepted and always ignored. The default page size stays the legacy
 * 15. `count` now starts at 1 — the legacy `count=0` silently became 15 rows.
 */
export const ListRiskImpactAreasQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(1)
    .max(LIST_RISK_IMPACT_AREAS_MAX_PAGE_SIZE)
    .default(LIST_RISK_IMPACT_AREAS_DEFAULT_PAGE_SIZE),
  /** Stringified JSON — only `id` (exact) and `description` (substring) map. */
  filter: z.string().max(512).optional(),
});

export type ListRiskImpactAreasQuery = z.infer<
  typeof ListRiskImpactAreasQuerySchema
>;

/**
 * One row of `GET /risk-analysis/impact-areas`, ported from the legacy
 * `impactArea` response definition. The legacy field names stay on the wire —
 * `hierarchyId` is `section_id` and `description` is `name` — because both rule
 * builders and the stored rule conditions read them. `name` is a nullable
 * column, so `description` is nullable too.
 */
export const RiskImpactAreaRowSchema = z.object({
  id: z.string(),
  hierarchyId: z.string(),
  description: z.string().nullable(),
});

export type RiskImpactAreaRow = z.infer<typeof RiskImpactAreaRowSchema>;
